import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, Trash2, RefreshCw, Star, GripVertical } from 'lucide-react';
import { MonsteraSpinner } from './MonsteraSpinner';

export const PhotoGallery = ({ photos, onDeletePhoto, onReplacePhoto, onSetCover, onReorder, onPhotoTap, isLocal, extraAction }) => {
  const [current, setCurrent] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  // First-time "drag to reorder" hint — shown each session until the user
  // performs their first reorder drag, then persisted off for good.
  const [showReorderHint, setShowReorderHint] = useState(() => {
    try { return localStorage.getItem('plant_reorder_hint_done') !== '1'; } catch { return true; }
  });
  const dismissReorderHint = useCallback(() => {
    setShowReorderHint(false);
    try { localStorage.setItem('plant_reorder_hint_done', '1'); } catch { /* no-op */ }
  }, []);
  const touchRef = useRef({ startX: 0, startY: 0 });
  // Keyboard reorder: after an arrow-key move the photos array reorders,
  // so refocus the moved thumbnail at its new index post-render.
  const thumbRefs = useRef([]);
  const pendingFocusRef = useRef(null);
  // FLIP animation for reorder — measure each thumbnail's position keyed by
  // a stable photo id, and when a swap moves it, invert+play a slide.
  const flipRefs = useRef(new Map());
  const prevRects = useRef(new Map());

  const total = photos?.length || 0;

  useLayoutEffect(() => {
    const newRects = new Map();
    flipRefs.current.forEach((el, id) => {
      if (!el) return;
      const rect = el.getBoundingClientRect();
      newRects.set(id, rect);
      const prev = prevRects.current.get(id);
      if (prev) {
        const dx = prev.left - rect.left;
        const dy = prev.top - rect.top;
        if (dx || dy) {
          el.style.transition = 'none';
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          requestAnimationFrame(() => {
            el.style.transition = 'transform 220ms cubic-bezier(0.2, 0, 0, 1)';
            el.style.transform = '';
          });
        }
      }
    });
    prevRects.current = newRects;
  });

  useEffect(() => {
    if (pendingFocusRef.current == null) return;
    const target = pendingFocusRef.current;
    setCurrent(target);
    const focusTarget = () => {
      const el = thumbRefs.current[target];
      if (el) el.focus({ preventScroll: true });
    };
    // Re-assert focus AFTER Radix's dialog focus-scope reconciles (double
    // rAF) and again after the async local-mode photo reload settles, so a
    // keyboard user can chain arrow-key reorders without focus snapping back
    // to the dialog container.
    let inner = 0;
    const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(focusTarget); });
    const tid = setTimeout(() => { focusTarget(); pendingFocusRef.current = null; }, 280);
    return () => { cancelAnimationFrame(outer); cancelAnimationFrame(inner); clearTimeout(tid); };
  }, [photos]);

  const movePhoto = (from, to, refocus = true) => {
    if (to < 0 || to >= total || from === to) return;
    onReorder(from, to);
    dismissReorderHint();
    if (refocus) pendingFocusRef.current = to;
    else setCurrent(to);
  };

  const handleThumbKeyDown = (e, i) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const to = e.key === 'ArrowLeft' ? i - 1 : i + 1;
    if (to < 0 || to >= total) return;
    e.preventDefault();
    movePhoto(i, to);
  };

  useEffect(() => {
    setCurrent(c => c >= total ? Math.max(0, total - 1) : c);
    setLoadedImages({});
  }, [total]);

  const handleImageLoad = useCallback((index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  }, []);

  if (!total) return null;

  // Clamp current in the current render pass too — the useEffect above
  // only fires post-render, so on the very first render after `photos`
  // shrinks (e.g. user just deleted the last photo they were viewing),
  // `current` can still be >= total and `photos[current]` would be
  // undefined. Reading `.src` on that would crash the whole modal.
  const safeCurrent = current >= total ? Math.max(0, total - 1) : current;
  const prev = () => setCurrent(c => ((c - 1 + total) % total));
  const next = () => setCurrent(c => ((c + 1) % total));

  const handleTouchStart = (e) => {
    touchRef.current.startX = e.touches[0].clientX;
    touchRef.current.startY = e.touches[0].clientY;
  };

  const handleTouchEnd = (e) => {
    if (total <= 1) return;
    const dx = e.changedTouches[0].clientX - touchRef.current.startX;
    const dy = e.changedTouches[0].clientY - touchRef.current.startY;
    // Only swipe if horizontal movement is dominant and exceeds threshold
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) next();
      else prev();
    }
  };

  const photo = photos[safeCurrent];
  if (!photo) return null;

  return (
    <div className="relative w-full" data-testid="photo-gallery" role="region" aria-roledescription="carousel" aria-label="Plant photo gallery">
      {/* Photo display */}
      <div
        className="relative w-full aspect-square max-h-[30vh] sm:max-h-[36vh] md:max-h-[32vh] lg:max-h-[28vh] bg-gray-100 rounded-lg overflow-hidden touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {!loadedImages[safeCurrent] && (
          <div className="absolute inset-0 flex items-center justify-center">
            <MonsteraSpinner size={40} />
          </div>
        )}
        <img
          src={photo.src}
          alt={total > 1 ? `Photo ${safeCurrent + 1} of ${total}` : 'Plant photo'}
          className={`w-full h-full object-contain transition-opacity cursor-pointer ${loadedImages[safeCurrent] ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => handleImageLoad(safeCurrent)}
          onClick={(e) => { e.stopPropagation(); if (onPhotoTap) onPhotoTap(safeCurrent); }}
          data-testid={`gallery-photo-${safeCurrent}`}
        />

        {/* Navigation arrows — 44×44 tap targets (iOS HIG minimum) with a slightly
            larger transparent hit area so taps near the visible chevron still
            register on the button rather than falling through to the image
            underneath (which would trigger the enlarge handler). */}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              className="absolute left-1 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
              style={{ touchAction: 'manipulation' }}
              aria-label="Previous photo"
              data-testid="gallery-prev-btn"
            >
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); next(); }}
              onPointerDown={(e) => e.stopPropagation()}
              onTouchEnd={(e) => e.stopPropagation()}
              className="absolute right-1 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/70 transition-colors"
              style={{ touchAction: 'manipulation' }}
              aria-label="Next photo"
              data-testid="gallery-next-btn"
            >
              <ChevronRight size={22} aria-hidden="true" />
            </button>
          </>
        )}

        {/* Photo counter */}
        {total > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs font-medium"
            aria-live="polite" aria-atomic="true"
            data-testid="gallery-counter"
          >
            {safeCurrent + 1} / {total}
          </div>
        )}
      </div>

      {/* Thumbnail strip — doubles as the drag/keyboard reorder control when
          `onReorder` is provided: click a thumb to view it, drag · arrow keys ·
          ◀ ▶ to reorder (the first photo is the cover). Falls back to a plain
          navigation tablist in read-only contexts. */}
      {total > 1 && (
        <>
          {onReorder && showReorderHint && (
            <div
              className="flex items-center gap-1.5 mt-3 mb-1 px-2.5 py-1 rounded-full text-xs w-fit mx-auto"
              style={{ backgroundColor: 'hsl(143, 35%, 94%)', color: 'hsl(143, 30%, 32%)' }}
              data-testid="reorder-hint"
            >
              <GripVertical size={13} aria-hidden="true" />
              <span>Drag or use arrow keys to reorder · first photo is the cover</span>
            </div>
          )}
          <div
            className="flex gap-1.5 mt-2 justify-center"
            style={onReorder ? { touchAction: 'none' } : undefined}
            role={onReorder ? undefined : 'tablist'}
            aria-label="Photo thumbnails"
            data-testid="gallery-thumbnails"
            onPointerMove={onReorder ? ((e) => {
              if (dragFrom === null) return;
              const el = document.elementFromPoint(e.clientX, e.clientY);
              const thumb = el && el.closest ? el.closest('[data-thumb-idx]') : null;
              if (thumb) setDragOver(parseInt(thumb.getAttribute('data-thumb-idx'), 10));
            }) : undefined}
            onPointerUp={onReorder ? (() => {
              if (dragFrom !== null && dragOver !== null && dragFrom !== dragOver) { onReorder(dragFrom, dragOver); dismissReorderHint(); }
              setDragFrom(null); setDragOver(null);
            }) : undefined}
            onPointerLeave={onReorder ? (() => { setDragFrom(null); setDragOver(null); }) : undefined}
          >
            {photos.map((p, i) => {
              const pid = p.driveId != null
                ? `d:${p.driveId}`
                : (p.src ? `s:${p.src.slice(-96)}` : `i:${p.photoIndex}`);
              return (
                <div
                  key={pid}
                  ref={(el) => { if (el) flipRefs.current.set(pid, el); else flipRefs.current.delete(pid); }}
                  className="relative flex-shrink-0"
                  style={{ width: 52, height: 52 }}
                >
                  <button
                    type="button"
                    ref={(el) => { thumbRefs.current[i] = el; }}
                    data-thumb-idx={i}
                    role={onReorder ? undefined : 'tab'}
                    aria-selected={i === safeCurrent}
                    data-testid={`gallery-thumb-${i}`}
                    onPointerDown={onReorder ? ((e) => { setDragFrom(i); setDragOver(i); try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {} }) : undefined}
                    onKeyDown={onReorder ? ((e) => handleThumbKeyDown(e, i)) : undefined}
                    onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
                    className="absolute inset-0 rounded-md overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-1 transition-all"
                    style={{
                      border: i === safeCurrent ? '2px solid hsl(143, 28%, 35%)' : '2px solid transparent',
                      opacity: dragFrom === i ? 0.4 : (i === safeCurrent ? 1 : 0.75),
                      outline: dragOver === i && dragFrom !== null && dragFrom !== i ? '2px dashed hsl(143, 45%, 40%)' : 'none',
                      cursor: onReorder ? 'grab' : 'pointer',
                    }}
                    aria-label={onReorder
                      ? `Photo ${i + 1} of ${total}${i === 0 ? ' (cover)' : ''} — drag or press left/right arrow to reorder`
                      : `View photo ${i + 1}`}
                  >
                    <img src={p.thumbnail || p.src} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                    {onReorder && i === 0 && (
                      <span className="absolute bottom-0 inset-x-0 text-center text-white" style={{ fontSize: 8, lineHeight: '11px', backgroundColor: 'rgba(0,0,0,0.6)' }}>Cover</span>
                    )}
                  </button>
                  {onReorder && i > 0 && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); movePhoto(i, i - 1, false); }}
                      className="absolute top-0 left-0 z-10 w-4 h-4 flex items-center justify-center rounded-br-md text-white"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                      aria-label={`Move photo ${i + 1} left`}
                      data-testid={`reorder-move-left-${i}`}
                    >
                      <ChevronLeft size={11} aria-hidden="true" />
                    </button>
                  )}
                  {onReorder && i < total - 1 && (
                    <button
                      type="button"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); movePhoto(i, i + 1, false); }}
                      className="absolute top-0 right-0 z-10 w-4 h-4 flex items-center justify-center rounded-bl-md text-white"
                      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
                      aria-label={`Move photo ${i + 1} right`}
                      data-testid={`reorder-move-right-${i}`}
                    >
                      <ChevronRight size={11} aria-hidden="true" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Photo actions — single row, icon-only on mobile, full labels sm+ */}
      {((onDeletePhoto || onReplacePhoto || onSetCover) && total > 0) || extraAction ? (
        <div className="mt-2 flex items-center justify-around gap-1.5 sm:gap-3 flex-nowrap px-1" data-testid="gallery-actions">
          {onSetCover && total > 1 && safeCurrent !== 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSetCover(safeCurrent); }}
              className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity px-1 py-1 min-h-[32px]"
              style={{ color: 'hsl(38, 75%, 40%)' }}
              aria-label={`Set photo ${safeCurrent + 1} as the cover`}
              data-testid="gallery-set-cover-btn"
            >
              <Star size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Set as cover</span>
              <span className="sm:hidden">Cover</span>
            </button>
          )}
          {onReplacePhoto && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onReplacePhoto(safeCurrent); }}
              className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity px-1 py-1 min-h-[32px]"
              style={{ color: 'hsl(143, 28%, 35%)' }}
              aria-label={total > 1 ? `Replace photo ${safeCurrent + 1} of ${total}` : 'Replace this photo'}
              data-testid="gallery-replace-photo-btn"
            >
              <RefreshCw size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Replace{total > 1 ? ` (${safeCurrent + 1}/${total})` : ''}</span>
              <span className="sm:hidden">Replace</span>
            </button>
          )}
          {onDeletePhoto && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDeletePhoto(safeCurrent); }}
              className="flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700 transition-colors px-1 py-1 min-h-[32px]"
              aria-label={total > 1 ? `Delete photo ${safeCurrent + 1} of ${total}` : 'Delete this photo'}
              data-testid="gallery-delete-photo-btn"
            >
              <Trash2 size={14} aria-hidden="true" />
              <span className="hidden sm:inline">Delete{total > 1 ? ` (${safeCurrent + 1}/${total})` : ''}</span>
              <span className="sm:hidden">Delete</span>
            </button>
          )}
          {extraAction && (
            <div className="flex-shrink-0">{extraAction}</div>
          )}
        </div>
      ) : null}

    </div>
  );
};
